import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin } from 'antd';
import classNames from 'classnames';
import styles from './organizational-structure.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import { ScreenTitle } from 'src/components/design-system';
import { useProfileFields, useUserGroupStats } from 'shared/hooks/profile-fields.hooks';
import { getLabelsList } from 'shared/store/selectors/labels.selector';
import { getLabels } from 'shared/store/actions/labels.actions';
import { AllSettingsButton } from '../../settings.page.component';
import { FieldCard } from './field-card';
import { LocationCard } from './location-card';
import { CHILD_FIELD_BY_FIELD, PARENT_FIELD_BY_FIELD } from './profile-field-relations';

/** A field is part of the dependency chain if it drives or depends on another. */
const isChainField = field =>
  !!(CHILD_FIELD_BY_FIELD[field.fieldKey] || PARENT_FIELD_BY_FIELD[field.fieldKey]);

/**
 * Fixed group order + their section-title / subtitle lang keys.
 */
const GROUPS = [
  {
    key: 'ORGANIZATIONAL',
    titleKey: 'GROUP_ORGANIZATIONAL',
    subtitleKey: 'GROUP_ORGANIZATIONAL_SUBTITLE',
  },
  {
    key: 'PROFESSIONAL',
    titleKey: 'GROUP_PROFESSIONAL',
    subtitleKey: 'GROUP_PROFESSIONAL_SUBTITLE',
  },
];

/**
 * Organizational Structure & Classifications settings page. Renders profile
 * fields grouped by `group` (fields already ordered by `order` server-side); the
 * Organizational group also composes a Location card from `organizationLabels`.
 *
 * Dependent fields (role, levelOfTraining) are driven by a linked selection:
 * picking a profession filters the Role card, picking a role filters the Level
 * card. `selection` is keyed by the parent field.
 */
export function OrganizationalStructurePage() {
  const dispatch = useDispatch();
  const lang = useSelector(getLang('SETTINGS'));
  const { data: byGroup, isLoading } = useProfileFields();
  const { data: stats } = useUserGroupStats();
  const locations = useSelector(getLabelsList);
  const [selection, setSelection] = useState({});

  useEffect(() => {
    dispatch(getLabels());
  }, [dispatch]);

  const allFields = useMemo(
    () => (byGroup ? [...(byGroup.ORGANIZATIONAL || []), ...(byGroup.PROFESSIONAL || [])] : []),
    [byGroup],
  );

  return (
    <div className={classNames(styles.root, 'new-design-system')}>
      <AllSettingsButton />
      <div className={styles.header}>
        <ScreenTitle
          title={lang.ORG_STRUCTURE_TITLE}
          subtitle={lang.ORG_STRUCTURE_SUBTITLE}
          containerStyle={styles.title}
        />
      </div>

      {isLoading ? (
        <div className={styles.loading} role="status" aria-label={lang.LOADING}>
          <Spin />
        </div>
      ) : (
        GROUPS.map(group => {
          const fields = byGroup?.[group.key] || [];
          const isOrganizational = group.key === 'ORGANIZATIONAL';
          const headingId = `org-group-${group.key}`;

          // Only the dependency chain (profession → role → level of training)
          // renders inside a single card with column dividers, signalling that
          // those fields belong together. Independent fields (e.g. specialty)
          // and the Location card stay as their own standalone cards.
          const chainFields = fields.filter(isChainField);
          const looseFields = fields.filter(field => !isChainField(field));

          const hasFields = chainFields.length > 0 || looseFields.length > 0;
          if (!hasFields && !(isOrganizational && locations.length > 0)) return null;

          return (
            <section key={group.key} className={styles.group} aria-labelledby={headingId}>
              <div className={styles.groupHeader}>
                <h2 id={headingId} className={styles.groupTitle}>
                  {lang[group.titleKey]}
                </h2>
                <p className={styles.groupSubtitle}>{lang[group.subtitleKey]}</p>
              </div>

              {chainFields.length > 0 && (
                <div className={styles.gridMerged} style={{ '--chain-cols': chainFields.length }}>
                  {chainFields.map(field => (
                    <FieldCard
                      key={field.id}
                      field={field}
                      allFields={allFields}
                      stats={stats}
                      selection={selection}
                      setSelection={setSelection}
                      merged
                    />
                  ))}
                </div>
              )}

              {(looseFields.length > 0 || isOrganizational) && (
                <div
                  className={classNames(styles.grid, {
                    [styles.gridStacked]: chainFields.length > 0,
                  })}>
                  {looseFields.map(field => (
                    <FieldCard
                      key={field.id}
                      field={field}
                      allFields={allFields}
                      stats={stats}
                      selection={selection}
                      setSelection={setSelection}
                    />
                  ))}
                  {isOrganizational && <LocationCard />}
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
